import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, IconButton, Collapse } from "@material-ui/core";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import DownloadingRoundedIcon from "@mui/icons-material/DownloadingRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";

/**
 * Maps pipeline step keys to icons.
 */
const STEP_ICONS = {
  understanding_query: PsychologyRoundedIcon,
  searching_documents: SearchRoundedIcon,
  analyzing_sources: DescriptionOutlinedIcon,
  generating_response: AutoAwesomeRoundedIcon,
  fetching_more: DownloadingRoundedIcon,
};

const useStyles = makeStyles(() => ({
  // ---- Bouncing dots ----
  "@keyframes bounceDot": {
    "0%, 60%, 100%": { transform: "translateY(0)" },
    "30%": { transform: "translateY(-6px)" },
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#c4c4d4",
    display: "inline-block",
    animation: "$bounceDot 1.4s ease-in-out infinite",
  },

  // ---- Pill ----
  pill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 32,
    paddingLeft: 14,
    paddingRight: 14,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.10)",
    backdropFilter: "blur(12px)",
    whiteSpace: "nowrap",
  },
  pillIcon: {
    fontSize: 16,
    color: "#c4c4d4",
    flexShrink: 0,
  },
  pillText: {
    fontSize: 13,
    fontWeight: 500,
    color: "#c4c4d4",
    fontFamily: "Poppins, sans-serif",
  },

  // ---- Stepper elements ----
  stepRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    position: "relative",
  },
  circleCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  },
  circle: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    zIndex: 1,
  },
  line: {
    width: 1,
    backgroundColor: "#2a2a3e",
    marginTop: 2,
  },
  stepLabel: {
    fontSize: 13,
    fontFamily: "Poppins, sans-serif",
    paddingTop: 1,
  },

  // ---- Expanded container ----
  expandedContainer: {
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: 8,
    padding: 10,
  },

  // ---- Complete summary row ----
  completedRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  completedIcon: {
    fontSize: 16,
    color: "#4ade80",
  },
  completedText: {
    fontSize: 13,
    fontWeight: 500,
    color: "#6b6b80",
    fontFamily: "Poppins, sans-serif",
  },

  chevron: {
    color: "#6b6b80",
    padding: 2,
  },
}));

/**
 * PipelineStatus — hybrid stepper-pill status indicator.
 *
 * Props:
 *   steps  – array of { step, message, status } where status is
 *            "completed" | "active" | "pending"
 *   isComplete – boolean, true once all pipeline steps are done and
 *                content streaming has begun
 *   sourceCount – number of sources processed (for the collapsed summary)
 */
const PipelineStatus = ({ steps = [], isComplete = false, sourceCount }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  if (!steps || steps.length === 0) return null;

  const activeStep = steps.find((s) => s.status === "active");
  const completedSteps = steps.filter((s) => s.status === "completed");
  const ActiveIcon = activeStep ? (STEP_ICONS[activeStep.step] || AutoAwesomeRoundedIcon) : null;

  // ───────── COMPLETE states ─────────
  if (isComplete) {
    if (expanded) {
      // State 4: Complete (Expanded) — full vertical stepper, all green
      return (
        <Box mb={1}>
          <Box
            className={classes.expandedContainer}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Box display="flex" alignItems="flex-start">
              <Box flex={1} display="flex" flexDirection="column" style={{ gap: 6 }}>
                {steps.map((s, i) => (
                  <Box key={s.step} className={classes.stepRow}>
                    <Box className={classes.circleCol}>
                      <Box
                        className={classes.circle}
                        style={{ backgroundColor: "#4ade80" }}
                      />
                      {i < steps.length - 1 && (
                        <Box className={classes.line} style={{ height: 20 }} />
                      )}
                    </Box>
                    <Typography
                      className={classes.stepLabel}
                      style={{ color: "#6b6b80" }}
                    >
                      {s.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                style={{ cursor: "pointer", padding: 4, flexShrink: 0 }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setExpanded(false);
                }}
              >
                <ExpandLessRoundedIcon style={{ fontSize: 18, color: "#8b8ba3" }} />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    // State 3: Complete (Collapsed) — green check + summary
    const summaryText = sourceCount
      ? `Processed ${sourceCount} source${sourceCount !== 1 ? "s" : ""}`
      : "Processing complete";

    return (
      <Box
        display="flex"
        alignItems="center"
        mb={1}
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(true)}
      >
        <Typography className={classes.completedText} style={{ flex: 1 }}>
          {summaryText}
        </Typography>
        <ExpandMoreRoundedIcon style={{ fontSize: 18, color: "#8b8ba3" }} />
      </Box>
    );
  }

  // ───────── IN-PROGRESS states ─────────
  if (expanded) {
    // State 2: In Progress (Expanded) — history + active pill
    return (
      <Box mb={1}>
        <Box
          className={classes.expandedContainer}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Box display="flex" alignItems="flex-start">
            <Box flex={1} display="flex" flexDirection="column" style={{ gap: 6 }}>
              {steps.map((s, i) => {
                const StepIcon = STEP_ICONS[s.step] || AutoAwesomeRoundedIcon;

                if (s.status === "active") {
                  // Active step — render as pill with bouncing dots
                  return (
                    <Box key={s.step} className={classes.stepRow}>
                      <Box className={classes.circleCol}>
                        <Box
                          className={classes.circle}
                          style={{
                            backgroundColor: "#60a5fa",
                            boxShadow: "0 0 8px rgba(96,165,250,0.5)",
                          }}
                        />
                        {i < steps.length - 1 && (
                          <Box className={classes.line} style={{ height: 46 }} />
                        )}
                      </Box>
                      <Box flex={1} style={{ marginTop: -2 }}>
                        <Box className={classes.pill}>
                          <StepIcon className={classes.pillIcon} />
                          <Typography className={classes.pillText}>
                            {s.message}
                          </Typography>
                        </Box>
                        {/* Bouncing dots */}
                        <Box display="flex" alignItems="center" style={{ gap: 5, paddingLeft: 14, marginTop: 6 }}>
                          <span className={classes.dot} style={{ animationDelay: "0ms" }} />
                          <span className={classes.dot} style={{ animationDelay: "150ms" }} />
                          <span className={classes.dot} style={{ animationDelay: "300ms" }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                // Completed or pending step — plain row
                return (
                  <Box key={s.step} className={classes.stepRow}>
                    <Box className={classes.circleCol}>
                      <Box
                        className={classes.circle}
                        style={{
                          backgroundColor:
                            s.status === "completed" ? "#4ade80" : "#3a3a4a",
                        }}
                      />
                      {i < steps.length - 1 && (
                        <Box className={classes.line} style={{ height: 20 }} />
                      )}
                    </Box>
                    <Typography
                      className={classes.stepLabel}
                      style={{
                        color: s.status === "completed" ? "#6b6b80" : "#3a3a4a",
                      }}
                    >
                      {s.message}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box
              style={{ cursor: "pointer", padding: 4, flexShrink: 0 }}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setExpanded(false);
              }}
            >
              <ExpandLessRoundedIcon style={{ fontSize: 18, color: "#8b8ba3" }} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // State 1: In Progress (Collapsed) — single pill + bouncing dots
  return (
    <Box mb={1}>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <Box className={classes.pill} style={{ flex: 1 }}>
          {ActiveIcon && <ActiveIcon className={classes.pillIcon} />}
          <Typography className={classes.pillText}>
            {activeStep ? activeStep.message : "Processing..."}
          </Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.chevron}
          onClick={() => setExpanded(true)}
        >
          <ExpandMoreRoundedIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {/* Bouncing dots */}
      <Box display="flex" alignItems="center" style={{ gap: 5, paddingLeft: 14, marginTop: 6 }}>
        <span className={classes.dot} style={{ animationDelay: "0ms" }} />
        <span className={classes.dot} style={{ animationDelay: "150ms" }} />
        <span className={classes.dot} style={{ animationDelay: "300ms" }} />
      </Box>
    </Box>
  );
};

export default PipelineStatus;
